<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { hasNextPage } from "@/utils/paginationUtils/paginationUtils";
    import HeartEmptyIcon from "@apps/wishlist/components/icons/HeartEmptyIcon.svelte";
    import { BUTTON_VARIANT } from "@apps/wishlist/components/WishlistTheme/constants";
    import { wishlistThemeContextToken } from "@apps/wishlist/components/WishlistTheme/WIshlistThemeContextToken";
    import { WISHLIST_ICON_TYPES } from "@apps/wishlist/constants/constants";
    import { WishlistWithItemsEntity } from "@apps/wishlist/entities/WishlistWithItemsEntity";
    import WishlistsBackInStockItemsQuery from "@apps/wishlist/queries/WishlistsBackInStockItemsQuery";
    import { wishlistApplicationLoggerToken } from "@apps/wishlist/tokens";
    import {
        WISHLIST_ITEMS_FILTERING_TYPE,
        WISHLIST_ITEMS_SORTING_TYPE,
    } from "@apps/wishlist/types";
    import FormManager from "@lib/FormManager/FormManager";
    import FormManagerController from "@lib/FormManager/FormManagerController.svelte";
    import FormManagerProvider from "@lib/FormManager/FormManagerProvider.svelte";
    import { ValidationStrategy } from "@lib/FormManager/types";
    import yupResolver from "@lib/FormManager/yupResolver";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { CommonModule } from "@modules/common_module/CommonModule";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
    import { ProductsWithQuantityRulesQuery } from "@modules/products/queries/ProductsWithQuantityRulesQuery";
    import CN from "classnames";
    import { map } from "rxjs";
    import { createEventDispatcher } from "svelte";
    import { container } from "tsyringe";
    import { object, string } from "yup";

    import Badge from "@components/Badge/Badge.svelte";
    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Button from "@components/Buttons/Button.svelte";
    import ButtonWithIcon from "@components/Buttons/ButtonWithIcon.svelte";
    import EmptyState from "@components/EmptyState/EmptyState.svelte";
    import BaseTextInput from "@components/forms/BaseTextInput/BaseTextInput.svelte";
    import Icon from "@components/Icon.svelte";
    import CheckIcon from "@components/icons/CheckIcon.svelte";
    import ChevronIcon from "@components/icons/ChevronIcon.svelte";
    import DeleteIcon from "@components/icons/DeleteIcon.svelte";
    import EditIcon from "@components/icons/EditIcon.svelte";
    import LoadMore from "@components/LoadMore.svelte";
    import T from "@components/T.svelte";
    import WishlistIcon from "@components/WishlistIcon/WishlistIcon.svelte";

    import { OpenedWishlistsUIStore } from "../../wishlists_widget/ui_stores/OpenedWishlistsUIStore";
    import WishlistPageItem from "./WishlistPageItem.svelte";

    export let wishlist: WishlistWithItemsEntity;
    export let wishlists: WishlistWithItemsEntity[];
    export let currentSort: WISHLIST_ITEMS_SORTING_TYPE;
    export let currentFilter: WISHLIST_ITEMS_FILTERING_TYPE =
        WISHLIST_ITEMS_FILTERING_TYPE.all;
    export let productsQuery: ProductsQuery;
    export let productsWithQuantityRulesQuery: ProductsWithQuantityRulesQuery;
    export let wishlistsBackInStockItemsQuery: WishlistsBackInStockItemsQuery;
    export let openedWishlistsUIStore: OpenedWishlistsUIStore;
    export let isSharedList: boolean;
    export let loadMoreStatus: STATUS;
    export let namespace: string;
    export let criticalNamespace: string;
    export let dynamicImportManager: DynamicImportManager;
    export let isCartAvailable: boolean;

    let isEditable = false;

    const dispatch = createEventDispatcher();
    const TitleFormScheme = object({
        title: string().min(2),
    });
    const logger = container.resolve(wishlistApplicationLoggerToken);
    const urlsBuilder = container.resolve(CommonModule).exports.urlsBuilder;
    const wishlistBranding = getTypedContext(wishlistThemeContextToken);

    const products$ = productsQuery.selectAll();
    let filteredWishlistsCount = 0;

    $: wishlistItems$ = products$.pipe(
        map((products) => {
            const productIds = new Set(products.map((product) => product.id));

            const wishlistItems = wishlist.items.filter((item) =>
                productIds.has(item.productId)
            );
            return wishlistItems;
        })
    );

    $: filteredWishlistItems$ = wishlistItems$.pipe(
        map((wishlistItems) => {
            if (currentFilter !== WISHLIST_ITEMS_FILTERING_TYPE.all) {
                const filteredItems = wishlistItems.filter((item) => {
                    const product = $products$.find(
                        (p) => p.id === item.productId
                    );
                    if (!product) return false;

                    const variant = product.variants.find(
                        (v) => v.id === item.variantId
                    );
                    return variant && variant.availableForSale;
                });

                filteredWishlistsCount = filteredItems.length;
                return filteredItems;
            } else {
                filteredWishlistsCount = wishlistItems.length;
                return wishlistItems;
            }
        })
    );

    const formManager = new FormManager(
        {
            initialValues: {
                title: wishlist.title,
            },
            unregisterOnUnmount: false,
            resolver: yupResolver(TitleFormScheme),
            validationStrategy: ValidationStrategy.OnChange,
            onSubmit: ({ values }) => {
                if (values) {
                    isEditable = false;
                    handleRenameWishlist(wishlist.id, values.title);
                }
            },
        },
        logger
    );

    let titleInputRef: HTMLInputElement | null = null;
    let isOpened$ = openedWishlistsUIStore.selectWishlistIsOpened(
        wishlist.id,
        true
    );

    const handleToggleEditable = () => {
        isEditable = !isEditable;
    };

    const handleToggle = () => {
        openedWishlistsUIStore.toggleWishlist(wishlist.id);
    };

    const handleDeleteItem = (
        wishlistId: number,
        productId: number,
        variantId: number
    ) => {
        dispatch("deleteItem", { wishlistId, productId, variantId });
    };

    const handleDeleteWishlist = (wishlistId: number) => {
        dispatch("deleteWishlist", { wishlistId });
    };

    const handleMoveItem = (
        wishlistId: number,
        productId: number,
        variantId: number,
        targetWishlistId: number
    ) => {
        dispatch("moveItem", {
            wishlistId,
            productId,
            variantId,
            targetWishlistId,
        });
    };

    const handleUpdateItemVariant = (
        wishlistId: number,
        productId: number,
        oldVariantId: number,
        newVariantId: number
    ) => {
        dispatch("updateItemVariant", {
            wishlistId,
            productId,
            oldVariantId,
            newVariantId,
        });
    };

    const handleRenameWishlist = (wishlistId: number, newTitle: string) => {
        dispatch("renameWishlist", { wishlistId, newTitle });
    };

    const handleLoadMore = (
        limit: number,
        offset: number,
        wishlistId: number,
        currentSort: WISHLIST_ITEMS_SORTING_TYPE
    ) => {
        dispatch("loadMore", {
            limit,
            offset,
            wishlistId,
            orderBy: currentSort,
        });
    };

    let showConfirmDelete = false;

    const toggleConfirmDelete = () => {
        showConfirmDelete = !showConfirmDelete;
    };

    $: {
        formManager.resetValues({
            title: wishlist.title,
        });
    }

    $: {
        if (isEditable && titleInputRef) {
            titleInputRef.focus();
            titleInputRef.selectionStart = 0;
        }
    }

    $: hasNextPage$ = hasNextPage({
        perPage: wishlist.perPage,
        currentOffset: wishlist.currentOffset,
        totalCount: wishlist.totalCount,
    });

    const i18nModule = container.resolve(I18nModule);

    const edit_wishlist_button_accessibility_label$ =
        i18nModule.exports.i18n.selectT(
            criticalNamespace,
            "edit_wishlist_button_accessibility_label"
        );
    const save_wishlist_button_accessibility_label$ =
        i18nModule.exports.i18n.selectT(
            criticalNamespace,
            "save_wishlist_button_accessibility_label"
        );
    const delete_wishlist_button_accessibility_label$ =
        i18nModule.exports.i18n.selectT(
            criticalNamespace,
            "delete_wishlist_button_accessibility_label"
        );
    const empty_state_title$ = i18nModule.exports.i18n.selectT(
        criticalNamespace,
        "empty_state_title"
    );
</script>

<div class="gw-wl-page-list">
    <div class="gw-wl-page-list__header">
        <div class="gw-wl-page-title-wrapper">
            {#if wishlists.length > 1}
                <ButtonWithIcon on:click={handleToggle}>
                    <Icon
                        color="#777E90"
                        width={12}
                        height={12}
                        class={$isOpened$
                            ? "gw-toggle-btn gw-toggle-btn-opened"
                            : "gw-toggle-btn gw-toggle-btn-closed"}
                    >
                        <ChevronIcon />
                    </Icon>
                </ButtonWithIcon>
            {/if}

            {#if !isEditable}
                <div class="gw-wl-page-list__header-title">
                    <BaseButton
                        class="gw-wl-page-list__header-title-btn"
                        on:click={handleToggle}
                    >
                        {wishlist.title}
                        {#if filteredWishlistsCount}
                            <Badge
                                variant="flat"
                                bold
                                size="lg"
                                backgroundColor="none"
                            >
                                {filteredWishlistsCount}
                            </Badge>
                        {/if}
                    </BaseButton>
                </div>
            {:else}
                <FormManagerProvider {formManager}>
                    <form on:submit|preventDefault={formManager.submit}>
                        <FormManagerController name="title" let:field>
                            <div class="gw-wl-page-list__title-input-wrapper">
                                <BaseTextInput
                                    bind:ref={titleInputRef}
                                    on:change={field.onChange}
                                    name={field.name}
                                    value={field.value}
                                    class={CN("gw-wl-page-list__title-input", {
                                        "--gw-with-error":
                                            field.errors && field.errors.length,
                                    })}
                                />
                            </div>
                        </FormManagerController>
                    </form>
                </FormManagerProvider>
            {/if}

            {#if !isSharedList}
                {#if !isEditable}
                    <ButtonWithIcon
                        class="gw-wl-page-header__action-btn"
                        on:click={handleToggleEditable}
                        ariaLabel={$edit_wishlist_button_accessibility_label$}
                    >
                        <Icon color="#777E90">
                            <EditIcon />
                        </Icon>
                    </ButtonWithIcon>
                {:else}
                    <ButtonWithIcon
                        on:click={formManager.submit}
                        ariaLabel={$save_wishlist_button_accessibility_label$}
                    >
                        <Icon color="#777E90">
                            <CheckIcon />
                        </Icon>
                    </ButtonWithIcon>
                {/if}
            {/if}
        </div>

        <div
            class={CN(
                "gw-wl-page-list-header__line",
                showConfirmDelete && "gw-wl-page-list-header__line--denger"
            )}
        />

        {#if !isSharedList}
            <ButtonWithIcon
                class="gw-wl-page-header__action-btn"
                on:click={toggleConfirmDelete}
                ariaLabel={$delete_wishlist_button_accessibility_label$}
            >
                <Icon color="#EF466F">
                    <DeleteIcon />
                </Icon>
            </ButtonWithIcon>
        {/if}
    </div>

    <div class="gw-wl-page-list-content">
        {#if !filteredWishlistsCount}
            <div class="gw-wl-page__empty-state-wrapper">
                <EmptyState title={$empty_state_title$}>
                    <svelte:fragment slot="icon">
                        {#if wishlistBranding.wishlistIconSettings.iconType === WISHLIST_ICON_TYPES.default && !wishlistBranding.wishlistIconSettings.customIcons.isEnabled}
                            <HeartEmptyIcon />
                        {:else}
                            <WishlistIcon
                                width={60}
                                height={60}
                                isFilled={false}
                                iconSettings={wishlistBranding.wishlistIconSettings}
                            />
                        {/if}
                    </svelte:fragment>
                    <a
                        href={urlsBuilder.createCollectionAllUrl()}
                        slot="footer"
                    >
                        <T
                            namespace={criticalNamespace}
                            translationKey="browse_products"
                        />
                    </a>
                </EmptyState>
            </div>
        {/if}

        <div
            class="gw-wl-page-list__items-wrapper"
            class:gw-opened={$isOpened$}
        >
            <ul class="gw-wl-page-list__items">
                {#each $filteredWishlistItems$ as item (new Set( [item.productId, item.variantId] ))}
                    <div class="gw-wl-page-list__item-wrapper">
                        <WishlistPageItem
                            {item}
                            {wishlist}
                            {wishlists}
                            {productsQuery}
                            {productsWithQuantityRulesQuery}
                            {wishlistsBackInStockItemsQuery}
                            on:deleteItem={() =>
                                handleDeleteItem(
                                    wishlist.id,
                                    item.productId,
                                    item.variantId
                                )}
                            on:moveItem={({ detail: { targetWishlistId } }) =>
                                handleMoveItem(
                                    wishlist.id,
                                    item.productId,
                                    item.variantId,
                                    targetWishlistId
                                )}
                            on:updateItemVariant={({
                                detail: { newVariantId },
                            }) =>
                                handleUpdateItemVariant(
                                    wishlist.id,
                                    item.productId,
                                    item.variantId,
                                    newVariantId
                                )}
                            isSharedItem={isSharedList}
                            {criticalNamespace}
                            {namespace}
                            {dynamicImportManager}
                            {isCartAvailable}
                        />
                    </div>
                {/each}
            </ul>

            {#if hasNextPage$}
                <LoadMore
                    loading={loadMoreStatus === STATUS.LOADING}
                    on:loadMore={() =>
                        handleLoadMore(
                            wishlist.perPage,
                            wishlist.perPage + wishlist.currentOffset,
                            wishlist.id,
                            currentSort
                        )}
                    class="gw-wl-page-load-more"
                    variant={BUTTON_VARIANT[
                        wishlistBranding.wishlistDrawerAndPageSettings
                            .buttonsStyle
                    ]}
                    size="large"
                >
                    <T {namespace} translationKey="load_more" />
                </LoadMore>
            {/if}
        </div>

        <div
            class={CN("gw-wl-page-delete-confirmation", {
                "gw-wl-page-delete-confirmation_visible": showConfirmDelete,
            })}
        >
            <div class="gw-wl-page-delete-confirmation__title">
                <T
                    {namespace}
                    translationKey="delete_wishlist_confirmation_title"
                />
            </div>
            <div class="gw-wl-page-delete-confirmation__actions">
                <Button
                    rounded
                    on:click={() => handleDeleteWishlist(wishlist.id)}
                >
                    <T
                        {namespace}
                        translationKey="delete_wishlist_confirmation_confirm_button_text"
                    />
                    <slot name="icon">
                        <Icon color="#ffffff">
                            <DeleteIcon />
                        </Icon>
                    </slot>
                </Button>
                <Button
                    rounded
                    variant="outlined"
                    on:click={toggleConfirmDelete}
                >
                    <T
                        {namespace}
                        translationKey="delete_wishlist_confirmation_cancel_button_text"
                    />
                </Button>
            </div>
        </div>
    </div>
</div>

<style lang="postcss">
    .gw-wl-page-list {
        :global(.gw-empty-state a) {
            color: var(--gw-wl-wishlistDrawerAndPageSettings-linkColor);
        }
    }

    .gw-wl-page-list__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
        padding: 0.5em 0;
    }

    .gw-wl-page-list__header-title {
        color: var(--gw-dark-text-color);
        font-size: 0.9em;
        font-weight: bold;
    }

    :global(.gw-wl-page-list__header-title-btn) {
        font-size: inherit;
        font-weight: inherit;
        text-transform: inherit;
        display: flex;
        gap: 4px;
        align-items: center;
        white-space: nowrap;
    }

    .gw-wl-page-list__header-title-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 0;
        margin: 0;
        font-size: inherit;
        font-weight: inherit;
        text-transform: inherit;
    }

    .gw-wl-page-list__title-input-wrapper {
        & :global(.gw-wl-page-list__title-input) {
            border: none;
            background: none;
            color: var(--gw-lightgrey-text-color);
            outline: none;
            box-shadow: none;
            font-size: 16px;
        }
        & :global(.--gw-with-error) {
            border: red 3px solid;
        }
    }

    .gw-wl-page-list__header :global(.gw-toggle-btn) {
        transform: rotateX(0deg);
        transition: transform var(--gw-default-transition-duration);
    }

    .gw-wl-page-list__header :global(.gw-toggle-btn-opened) {
        transform: rotateX(-180deg);
    }

    .gw-wl-page__empty-state-wrapper {
        display: flex;
        justify-content: center;
        width: 100%;
    }

    .gw-wl-page-list__items-wrapper {
        display: none;
        margin: 0;
        padding: 0;
    }

    .gw-wl-page-list__items-wrapper.gw-opened {
        display: block;
    }

    .gw-wl-page-list__items {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        padding: 0;

        @media all and (max-width: 480px) {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    .gw-wl-page-list__item-wrapper {
    }

    .gw-wl-page-title-wrapper {
        display: flex;
        align-items: center;
        gap: 5px;
    }

    .gw-wl-page-list-header__line {
        height: 1px;
        width: 100%;
        background: #e6e8ec;
        transform: all 0.3s;

        &--denger {
            background: #ef466f;
        }
    }

    .gw-wl-page-list-content {
        position: relative;

        :global(.gw-wl-page-load-more) {
            margin-top: 4rem;
            padding: 12px 32px;
            font-weight: 700;
        }

        :global(.gw-wl-page-load-more.--outlined) {
            color: var(--gw-theme-main-text-primary);
        }
    }

    .gw-wl-page-delete-confirmation {
        padding: 16px 0;
        position: absolute;
        z-index: 10;
        top: 0;
        left: 0;
        width: 100%;
        background: #fff;
        box-shadow: 0 0 10px 10px #fff;
        transform: translateY(40px);
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s;
    }

    .gw-wl-page-delete-confirmation_visible {
        visibility: visible;
        opacity: 1;
        transform: none;
    }

    .gw-wl-page-delete-confirmation__title {
        margin-bottom: 16px;
        font-size: 14px;
        font-weight: bold;
        text-align: center;
    }

    .gw-wl-page-delete-confirmation__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        justify-content: center;

        :global(.gw-btn.--contained) {
            background-color: #ef466f;
            color: #fff;
        }

        :global(.gw-btn.--outlined) {
            color: #000;
            border: 2px solid #e6e8ec;
            box-shadow: none;
        }
    }
</style>
